import axios from 'axios'
import { toast } from 'react-hot-toast'
import { DNA_BANNER_CODES, foutData, foutTekstVan } from '../utils/apiFout'

/**
 * Get Bearer token from orion-auth storage (consistent with authStore).
 * Use for raw fetch() calls that need Authorization header.
 */
export function getAuthToken() {
  try {
    const raw = localStorage.getItem('orion-auth')
    if (!raw) return null
    const data = JSON.parse(raw)
    return data?.state?.token ?? null
  } catch {
    return null
  }
}

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout (default)
  withCredentials: true, // Stuur cookies mee (nodig voor CSRF)
})

/**
 * Lees CSRF-token uit cookie (double-submit pattern).
 */
export function getCsrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)orion_csrf_token=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Headers voor een fetch buiten axios (een Web Worker): Bearer-token en CSRF-token. Een worker
 * kan document.cookie niet lezen, dus de pagina geeft beide mee (#681).
 */
export function workerAuthHeaders() {
  const token = getAuthToken()
  const csrf = getCsrfToken()
  return {
    Authorization: token ? `Bearer ${token}` : '',
    ...(csrf ? { 'x-csrf-token': csrf } : {}),
  }
}

// Extended timeout for heavy operations (PDF exports, calculations)
const HEAVY_OPERATION_TIMEOUT = 60000 // 60 seconds

/**
 * Check if request is a heavy operation (PDF, calculations)
 * Note: analytics/profitability en auth/me gebruiken korte timeout + fallback (fail-fast)
 */
const isHeavyOperation = (url) => {
  const heavyPatterns = [
    '/pdf/',
    '/export/',
    '/calculate',
    '/calculation',
    '/generate',
    '/batch',
    '/validate-formula',
    '/plaat-presets',
    '/fronts',
    '/materialen',
    '/ai/process',  // AI verwerking kan lang duren (LLM calls)
    '/ai/process/stream',
    '/ai/analyze',
    '/ai/analyze/stream',
  ]
  return heavyPatterns.some(pattern => url.includes(pattern))
}

// Retry logic voor netwerkfouten (niet voor 5xx - backend-bugs retryen helpt niet)
const MAX_RETRIES = 2

const retryRequest = async (error) => {
  const config = error.config
  
  if (!config) {
    return Promise.reject(error)
  }

  config.__retryCount = config.__retryCount || 0
  
  if (config.__retryCount >= MAX_RETRIES) {
    config.__retryCount = 0
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[API] Max retries (${MAX_RETRIES}) bereikt voor ${config.url || 'unknown endpoint'}`)
    }
    return Promise.reject(error)
  }

  // Alleen retry bij netwerkfouten (geen verbinding). 5xx retryen = console noise, geen voordeel.
  const isNetworkError = !error.response && error.request
  if (!isNetworkError) {
    config.__retryCount = 0
    return Promise.reject(error)
  }

  config.__retryCount++
  const delay = Math.pow(2, config.__retryCount - 1) * 1000
  
  if (process.env.NODE_ENV === 'development') {
    console.debug(`[API] Retry (${config.__retryCount}/${MAX_RETRIES}) voor ${config.url || 'unknown endpoint'} na ${delay}ms`)
  }
  
  await new Promise(resolve => setTimeout(resolve, delay))
  return api(config)
}

// Request interceptor with throttling and timeout management
api.interceptors.request.use(
  (config) => {
    // FormData: verwijder Content-Type zodat browser multipart/form-data met boundary zet
    // Anders: Content-Type: application/json + FormData body → 422 Unprocessable Entity
    if (config.data instanceof FormData && config.headers) {
      const h = config.headers
      if (typeof h === 'object') {
        delete h['Content-Type']
        delete h['content-type']
      }
    }

    // Add authentication token
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // CSRF token meesturen bij mutatie-requests (POST/PUT/DELETE/PATCH)
    const method = (config.method || 'get').toUpperCase()
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      const csrfToken = getCsrfToken()
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken
      }
    }
    
    // Increase timeout for heavy operations
    if (isHeavyOperation(config.url || '')) {
      config.timeout = HEAVY_OPERATION_TIMEOUT
      // Add header to indicate this is a heavy operation
      config.headers['X-Heavy-Operation'] = 'true'
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor with retry logic and cleanup
api.interceptors.response.use(
  (response) => {
    // Reset retry count on success
    if (response.config) {
      response.config.__retryCount = 0
    }
    return response
  },
  async (error) => {
    const config = error.config

    // Log and show 500 errors for debugging (backend returns error detail in development)
    if (error.response?.status === 500) {
      const data = error.response?.data
      // Altijd TEKST (utils/apiFout): een detail-object ({code, message}) als toast-bericht
      // gaf een React-renderfout. Tekstantwoord: die tekst.
      const msg = typeof data === 'string' && data
        ? data
        : foutTekstVan(error, 'Geen details (check backend logs)')
      console.error('[API 500]', config?.url, '→', msg)
      // Altijd volledige response loggen in dev voor debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('[API 500] volledige response:', data != null ? JSON.stringify(data, null, 2) : '(leeg - check of backend op juiste poort draait, bv. 8001)')
        if (data?.traceback) {
          console.error('[API 500] traceback:', data.traceback)
        }
      }
      // In development: show toast once so user sees the actual backend error (avoid spam)
      if (process.env.NODE_ENV === 'development') {
        const now = Date.now()
        if (!api._last500Toast || now - api._last500Toast > 5000) {
          const shortMsg = typeof msg === 'string' && msg.length > 200 ? msg.slice(0, 200) + '…' : msg
          toast.error(`Serverfout (500): ${shortMsg}`, { duration: 15000 })
          api._last500Toast = now
        }
      }
    }

    // 503 ConfigurationError: DNA not configured - show DNA Integrity Banner instead of generic toast
    if (error.response?.status === 503) {
      const data = error.response?.data
      // De globale handler geeft { error: true, type: 'ConfigurationError', error_type:
      // 'DNA_NOT_CONFIGURED', message, ... }; `data.error === 'ConfigurationError'` was dus
      // nooit waar en de banner verscheen nooit. Een fout MET code (OFFERTE_PDF_*,
      // FINANCIEEL_*, ...) is geen ontbrekend DNA maar een blokkade van dat document: die
      // toont het scherm zelf met zijn code, niet de DNA-banner. Uitzondering: DNA_BANNER_CODES
      // (bv. BTW_TARIEVEN_ONTBREKEN) zijn wel een ontbrekende DNA-instelling.
      if (data && typeof data === 'object'
          && (data.type === 'ConfigurationError' || data.error_type === 'DNA_NOT_CONFIGURED')
          && (!data.code || DNA_BANNER_CODES.has(data.code))) {
        try {
          const { useDNAIntegrityStore } = await import('../stores/dnaIntegrityStore')
          useDNAIntegrityStore.getState().setConfigurationError({
            detail: data.message,
            incomplete_sections: data.incomplete_sections,
          })
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[API] Could not set DNA integrity store:', e)
          }
        }
        if (config) config.__retryCount = 0
        return Promise.reject(error)
      }

      // 503 ASYNC_WORKERS_UNAVAILABLE: backend gate weigert async-endpoints
      // zolang er geen Celery-worker draait. Toon een duidelijke toast zodat
      // gebruikers weten dat hun "verzend via async"-actie niet verwerkt is.
      // Detail is bij FastAPI genest onder data.detail.
      // Het object staat in detail_data (detail zelf is tekst); foutData leest beide.
      const detail = foutData(error)
      if (detail && detail.code === 'ASYNC_WORKERS_UNAVAILABLE') {
        const retryAfter = error.response.headers?.['retry-after']
        const hint = retryAfter ? ` Probeer opnieuw over ${retryAfter}s.` : ''
        const now = Date.now()
        if (!api._lastAsyncToast || now - api._lastAsyncToast > 5000) {
          toast.error(
            `Async verwerking is tijdelijk niet actief. Gebruik de directe (sync) variant.${hint}`,
            { duration: 8000 }
          )
          api._lastAsyncToast = now
        }
        if (config) config.__retryCount = 0
        return Promise.reject(error)
      }
    }

    if (error.response?.status === 401) {
      const url = config?.url || ''
      const isAuthMe = url.includes('/auth/me')
      const isLoginCall = url.includes('/auth/login')
      const isAlreadyOnLogin = window.location.pathname === '/login'

      // Sessie-expiratie: redirect naar login bij /auth/me-401 OF een aantoonbaar
      // verlopen token (JWT exp in het verleden). Ambigue 401's met een nog geldig
      // token (bv. 307 die de Auth-header stript) laten we met rust -> geen valse logout.
      let tokenExpired = false
      try {
        const raw = localStorage.getItem('orion-auth')
        const tok = raw ? JSON.parse(raw)?.state?.token : null
        if (tok) {
          const payload = JSON.parse(atob(tok.split('.')[1]))
          tokenExpired = !!payload?.exp && payload.exp * 1000 < Date.now()
        }
      } catch { /* negeer decode-fouten */ }

      if (!isLoginCall && !isAlreadyOnLogin && (isAuthMe || tokenExpired) && !api.__redirectingToLogin) {
        api.__redirectingToLogin = true
        localStorage.removeItem('orion-auth')
        window.location.href = '/login'
      }
      if (config) {
        config.__retryCount = 0
      }
      return Promise.reject(error)
    }

    // 403 Forbidden – Upgrade vereist (tier-gating); niet voor auth-gerelateerde 403
    if (error.response?.status === 403) {
      // detail is tekst; code staat bovenaan en het object in detail_data.
      const detail = error.response?.data?.detail
      const code = error.response?.data?.code || foutData(error)?.code || null
      const isUpgradeRequired = code === 'UPGRADE_REQUIRED' ||
        (typeof detail === 'string' && /upgrade|verplicht/i.test(detail))
      if (isUpgradeRequired) {
        const msg = (typeof detail === 'string' && detail) || foutData(error)?.message
          || 'Upgrade naar een hoger abonnement vereist.'
        try {
          const { useUpgradeModalStore } = await import('../stores/upgradeModalStore')
          useUpgradeModalStore.getState().show(msg)
        } catch {
          toast.error(msg, { duration: 8000 })
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Geen toegang tot deze actie.', { duration: 6000 })
      }
      if (config) config.__retryCount = 0
      return Promise.reject(error)
    }

    // 422 op GET /users/me: geen toast; lokale state (bijv. ToS geaccepteerd) prevaleert
    if (error.response?.status === 422 && config?.url && String(config.url).includes('users/me')) {
      if (config) config.__retryCount = 0
      return Promise.reject(error)
    }
    
    // Silently ignore 405 errors for GET requests to POST-only endpoints
    if (error.response?.status === 405 && config?.method?.toLowerCase() === 'get') {
      if (process.env.NODE_ENV === 'development') {
        console.debug('405 Method Not Allowed for GET request (expected):', config?.url)
      }
      if (config) {
        config.__retryCount = 0
      }
      return Promise.reject(error)
    }
    
    // Handle timeout errors with user-friendly message
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      const isHeavy = config?.headers?.['X-Heavy-Operation'] === 'true'
      const endpoint = config?.url || 'onbekend endpoint'

      // Fail-fast endpoints hebben eigen .catch() + fallback – geen toast tonen
      const silentTimeoutPatterns = ['/auth/me', '/users/me', '/setup/dna/status', '/analytics/', '/profitability']
      const isSilent = config?.__silentTimeout || silentTimeoutPatterns.some(p => endpoint.includes(p))

      const errorMessage = isHeavy
        ? 'De operatie duurt langer dan verwacht. Dit kan gebeuren bij grote PDF\'s of complexe berekeningen. Probeer het opnieuw of neem contact op met support.'
        : `De server reageert te traag (${endpoint}). Controleer of de backend actief is en probeer het opnieuw.`

      // Only show toast if not already shown (prevent spam) and not a silent endpoint
      if (!config.__timeoutToastShown && !isSilent) {
        toast.error(errorMessage, { duration: 8000 })
        config.__timeoutToastShown = true
      }
      
      if (config) {
        config.__retryCount = 0
      }
      return Promise.reject(error)
    }
    
    // Handle network errors
    if (!error.response && error.request) {
      const errorMessage = 'Geen verbinding met de server. Controleer je internetverbinding en probeer het opnieuw.'
      if (!config?.__networkErrorToastShown) {
        toast.error(errorMessage, { duration: 6000 })
        if (config) {
          config.__networkErrorToastShown = true
        }
      }
    }
    
    // Attempt retry for network/server errors
    try {
      return await retryRequest(error)
    } catch (retryError) {
      if (retryError.config) {
        retryError.config.__retryCount = 0
      }
      
      // Show final error message if all retries failed
      if (retryError.response) {
        // Altijd tekst met de code erbij (utils/apiFout): een detail-object als
        // toast-bericht gaf een React-renderfout.
        const errorMessage = foutTekstVan(retryError, 'Er is een fout opgetreden. Probeer het opnieuw.')
        if (!retryError.config?.__finalErrorToastShown) {
          toast.error(errorMessage, { duration: 6000 })
          if (retryError.config) {
            retryError.config.__finalErrorToastShown = true
          }
        }
      }
      
      return Promise.reject(retryError)
    }
  }
)

/**
 * Stuur draw-commando naar SketchUp voor gegeven cabinet_id.
 * Roept POST /api/integrations/sketchup/draw/{cabinet_id} aan.
 * BaseURL override: integrations-router staat onder /api (niet /api/v1).
 *
 * @param {number} cabinetId - SavedCabinet ID
 * @returns {Promise<{status: string, clients_notified: number}>}
 */
export async function drawSketchUpCabinet(cabinetId) {
  if (cabinetId == null || cabinetId === '' || Number.isNaN(Number(cabinetId))) return
  const res = await api.post(`/integrations/sketchup/draw/${cabinetId}`, {}, { baseURL: '/api' })
  return res.data
}

/**
 * Stuur lock-signaal naar SketchUp: kast wordt bewerkt in Orion, voorkom verplaatsen.
 */
export async function lockSketchUpCabinet(cabinetId) {
  if (cabinetId == null || cabinetId === '' || Number.isNaN(Number(cabinetId))) return
  const res = await api.post(`/integrations/sketchup/lock/${cabinetId}`, {}, { baseURL: '/api' })
  return res.data
}

/**
 * Stuur unlock-signaal naar SketchUp: bewerken afgerond.
 */
export async function unlockSketchUpCabinet(cabinetId) {
  if (cabinetId == null || cabinetId === '' || Number.isNaN(Number(cabinetId))) return
  const res = await api.post(`/integrations/sketchup/unlock/${cabinetId}`, {}, { baseURL: '/api' })
  return res.data
}

export default api
